/**
 * usePiecePlacement Hook
 *
 * Manages piece placement state and interactions for the setup phase:
 * - Piece selection from available pieces
 * - Rotation controls
 * - Click-to-place interaction
 * - Placement validation
 * - Placement locking (after game starts)
 */

import { useState, useCallback, useEffect } from 'react';
import {
  getAllPieceTypes,
  getPieceDefinition,
} from '../utils/pieceDefinitions';
import { rotatePiece } from '../utils/pieceRotation';
import {
  validatePlacement,
  areAllPiecesPlaced as checkAllPiecesPlaced,
} from '../utils/placementValidation';
import { useGameStore } from '../stores/gameStore';

export default function usePiecePlacement() {
  // Initialize available pieces (all 5 types)
  const [availablePieces, setAvailablePieces] = useState(() => {
    return getAllPieceTypes().map((type) => getPieceDefinition(type));
  });

  // Initialize placed pieces - but only if they belong to current session
  const [placedPieces, setPlacedPieces] = useState(() => {
    const state = useGameStore.getState();
    // Don't initialize with pieces if we haven't set sessionId yet
    // The restoration will happen in the useEffect below after session validation
    return [];
  });

  // Track if we've initialized from persisted state
  const [hasInitialized, setHasInitialized] = useState(false);

  // Track current session to detect changes
  const currentSessionId = useGameStore((state) => state.sessionId);
  const persistedSessionId = useGameStore((state) => state.persistedSessionId);
  const [lastSessionId, setLastSessionId] = useState(currentSessionId);

  // Reset pieces when session changes (user joined different game)
  useEffect(() => {
    if (
      lastSessionId &&
      currentSessionId &&
      lastSessionId !== currentSessionId
    ) {
      console.log(
        '[usePiecePlacement] Session changed, clearing pieces',
        lastSessionId,
        '->',
        currentSessionId
      );
      setPlacedPieces([]);
      setAvailablePieces(
        getAllPieceTypes().map((type) => getPieceDefinition(type))
      );
      setSelectedPiece(getPieceDefinition(getAllPieceTypes()[0]));
      setHasInitialized(false);
    }
    setLastSessionId(currentSessionId);
  }, [currentSessionId, lastSessionId]);

  // On mount, restore persisted pieces from gameStore if any
  // BUT only if they belong to the current session
  useEffect(() => {
    if (!hasInitialized && currentSessionId) {
      const state = useGameStore.getState();
      const persistedPieces = state.myPlacedPieces;

      // Only restore pieces if they belong to the current session
      // This prevents showing old pieces when joining a new game
      const isSameSession = state.persistedSessionId === currentSessionId;

      if (isSameSession && persistedPieces && persistedPieces.length > 0) {
        console.log(
          '[usePiecePlacement] Restoring',
          persistedPieces.length,
          'persisted pieces for session',
          currentSessionId
        );
        setPlacedPieces(persistedPieces);

        // Update available pieces to exclude already-placed pieces
        const placedTypes = persistedPieces.map((p) => p.type);
        const remaining = getAllPieceTypes()
          .filter((type) => !placedTypes.includes(type))
          .map((type) => getPieceDefinition(type));
        setAvailablePieces(remaining);

        // Update selected piece to first available piece (or null if all placed)
        if (remaining.length > 0) {
          setSelectedPiece(remaining[0]);
        } else {
          setSelectedPiece(null);
        }
      } else if (!isSameSession) {
        console.log(
          '[usePiecePlacement] Skipping restoration - different session',
          'persisted:',
          state.persistedSessionId,
          'current:',
          currentSessionId
        );
      }
      setHasInitialized(true);
    }
  }, [hasInitialized, currentSessionId]);

  // Currently selected piece for placement
  const [selectedPiece, setSelectedPiece] = useState(() => {
    // If rejoining with all pieces placed, don't select any piece
    const persistedPieces = useGameStore.getState().myPlacedPieces || [];
    if (persistedPieces.length >= 5) {
      return null;
    }
    const firstPiece = getAllPieceTypes()[0];
    return getPieceDefinition(firstPiece);
  });

  // Current rotation (0, 1, 2, 3)
  const [currentRotation, setCurrentRotation] = useState(0);

  // Placement locked (after clicking Play)
  const [isPlacementLocked, setIsPlacementLocked] = useState(false);

  // Error message for invalid placement
  const [errorMessage, setErrorMessage] = useState(null);

  // Preview position (for hover feedback)
  const [previewPosition, setPreviewPositionState] = useState(null);

  // Is preview position valid?
  const [isPreviewValid, setIsPreviewValid] = useState(true);

  /**
   * Get rotated shape for current piece and rotation
   */
  const getRotatedShape = useCallback((piece, rotation) => {
    let rotatedShape = piece.shape;
    for (let index = 0; index < rotation; index++) {
      rotatedShape = rotatePiece(rotatedShape, 'cw');
    }
    return rotatedShape;
  }, []);

  /**
   * Select a piece from available pieces
   */
  const selectPiece = useCallback(
    (pieceType) => {
      // Don't allow selection if locked
      if (isPlacementLocked) {
        return;
      }

      // Check if piece is available
      const piece = availablePieces.find((p) => p.type === pieceType);
      if (!piece) {
        return; // Piece already placed
      }

      setSelectedPiece(piece);
      setCurrentRotation(0); // Reset rotation for new piece
      setErrorMessage(null);
    },
    [availablePieces, isPlacementLocked]
  );

  /**
   * Rotate the selected piece clockwise
   */
  const rotatePieceHandler = useCallback(() => {
    if (isPlacementLocked) {
      return;
    }

    setCurrentRotation((prev) => (prev + 1) % 4);
    setErrorMessage(null);
  }, [isPlacementLocked]);

  /**
   * Place the selected piece at given position
   */
  const placePiece = useCallback(
    (position) => {
      if (isPlacementLocked) {
        setErrorMessage('Placement is locked. Game has started.');
        return;
      }

      if (!selectedPiece) {
        setErrorMessage('No piece selected.');
        return;
      }

      // Get rotated shape
      const rotatedShape = getRotatedShape(selectedPiece, currentRotation);

      // Create piece object
      const newPiece = {
        type: selectedPiece.type,
        shape: rotatedShape,
        position,
        rotation: currentRotation,
        color: selectedPiece.color,
        name: selectedPiece.name,
      };

      // Validate placement
      const validation = validatePlacement(newPiece, placedPieces);

      if (!validation.valid) {
        setErrorMessage(validation.error);
        return;
      }

      // Place the piece
      setPlacedPieces((prev) => [...prev, newPiece]);

      // Remove from available pieces
      setAvailablePieces((prev) =>
        prev.filter((p) => p.type !== selectedPiece.type)
      );

      // Auto-select next available piece
      const remaining = availablePieces.filter(
        (p) => p.type !== selectedPiece.type
      );
      if (remaining.length > 0) {
        setSelectedPiece(remaining[0]);
      } else {
        setSelectedPiece(null);
      }

      // Reset rotation and clear error
      setCurrentRotation(0);
      setErrorMessage(null);
    },
    [
      selectedPiece,
      currentRotation,
      placedPieces,
      availablePieces,
      isPlacementLocked,
      getRotatedShape,
    ]
  );

  /**
   * Remove a placed piece by index (pick it up to move it)
   */
  const removePiece = useCallback(
    (pieceIndex) => {
      if (isPlacementLocked) {
        return;
      }

      const pieceToRemove = placedPieces[pieceIndex];
      if (!pieceToRemove) {
        return;
      }

      // Remove from placed pieces
      setPlacedPieces((prev) =>
        prev.filter((_, index) => index !== pieceIndex)
      );

      // "Pick up" the piece - add back to available pieces and select it
      const pieceDefinition = getPieceDefinition(pieceToRemove.type);
      setAvailablePieces((prev) => [...prev, pieceDefinition]);
      setSelectedPiece(pieceDefinition);
      setCurrentRotation(pieceToRemove.rotation);
      setErrorMessage(null);
    },
    [placedPieces, isPlacementLocked]
  );

  /**
   * Lock placement (called when game starts)
   */
  const lockPlacement = useCallback(() => {
    setIsPlacementLocked(true);
  }, []);

  /**
   * Unlock placement (called when player ready status is reset)
   */
  const unlockPlacement = useCallback(() => {
    setIsPlacementLocked(false);
  }, []);

  /**
   * Check if all pieces are placed
   */
  const areAllPiecesPlaced = useCallback(() => {
    return checkAllPiecesPlaced(placedPieces);
  }, [placedPieces]);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    const allPieces = getAllPieceTypes().map((type) =>
      getPieceDefinition(type)
    );
    setAvailablePieces(allPieces);
    setPlacedPieces([]);
    setSelectedPiece(allPieces[0]);
    setCurrentRotation(0);
    setIsPlacementLocked(false);
    setErrorMessage(null);
    setPreviewPositionState(null);
    setIsPreviewValid(true);
  }, []);

  /**
   * Set preview position for hover feedback
   */
  const setPreviewPosition = useCallback(
    (position) => {
      setPreviewPositionState(position);

      if (!position || !selectedPiece) {
        setIsPreviewValid(true);
        return;
      }

      // Validate preview position
      const rotatedShape = getRotatedShape(selectedPiece, currentRotation);
      const previewPiece = {
        type: selectedPiece.type,
        shape: rotatedShape,
        position,
        rotation: currentRotation,
      };

      const validation = validatePlacement(previewPiece, placedPieces);
      setIsPreviewValid(validation.valid);
    },
    [selectedPiece, currentRotation, placedPieces, getRotatedShape]
  );

  /**
   * Clear preview position
   */
  const clearPreview = useCallback(() => {
    setPreviewPositionState(null);
    setIsPreviewValid(true);
  }, []);

  return {
    // State
    placedPieces,
    selectedPiece: selectedPiece
      ? {
          ...selectedPiece,
          shape: getRotatedShape(selectedPiece, currentRotation),
          rotation: currentRotation,
        }
      : null,
    availablePieces,
    currentRotation,
    isPlacementLocked,
    errorMessage,
    previewPosition,
    isPreviewValid,

    // Actions
    selectPiece,
    rotatePiece: rotatePieceHandler,
    placePiece,
    removePiece,
    lockPlacement,
    unlockPlacement,
    areAllPiecesPlaced,
    reset,
    setPreviewPosition,
    clearPreview,
  };
}
